/**
 * RejectionAuditTrail — surfaces a lightweight timeline when a candidate
 * WAS rejected but the rejection has since been reverted (`is_revert=true`
 * in `status_history`).
 *
 * CEO 2026-09-07 : « après avoir cliqué Annuler le refus, je veux voir
 * une trace claire "Refusé par X le Y — Refus annulé par Z le W" plutôt
 * que le refus qui disparaît sans laisser de mention. »
 *
 * Renders `null` when :
 *   - the candidate is currently rejected (the big red banner already
 *     tells the whole story), OR
 *   - the candidate has never been rejected in its history.
 */
import { CheckCircle2, ThumbsDown } from "lucide-react";

import { formatDateTime } from "../../lib/format";

const REASON_LABELS_MAP = {
    // Contact
    injoignable: "Numéro injoignable",
    plus_disponible: "Plus disponible",
    // Critères
    pas_permis_conduire: "Pas de permis de conduire",
    pas_disponibilite_horaire: "Pas de disponibilité horaire",
    distance_trop_grande: "Trop loin",
    pas_permis_travail: "Pas de titre de séjour",
    age_hors_criteres: "Âge hors critères",
    // Profil
    pas_motivation: "Manque de motivation",
    experience_insuffisante: "Expérience insuffisante",
    probleme_communication: "Problème de communication",
    profil_ne_correspond_pas: "Profil ne correspond pas",
    deja_embauche_ailleurs: "Déjà embauché ailleurs",
    // Autre
    criteres_non_remplis: "Critères non remplis",
    autre: "Autre motif",
    // Post-immersion
    immersion_inadequat: "Post-immersion — inadapté au terrain",
    immersion_comportement: "Post-immersion — comportement inadéquat",
    immersion_no_show: "Post-immersion — non présenté",
    immersion_candidat_retire: "Post-immersion — candidat retiré",
};


export default function RejectionAuditTrail({ candidate }) {
    if (!candidate) return null;
    // Only show the trail once the rejection has been undone. When the
    // candidate is still rejected, the red banner + header badge already
    // carry the info.
    if (candidate.status === "rejected") return null;

    const history = Array.isArray(candidate.status_history) ? candidate.status_history : [];
    if (history.length === 0) return null;

    // Walk the history in reverse. We want the MOST RECENT rejection
    // cycle: last "to_status=rejected" entry, and the very next
    // `is_revert && from_status=rejected` entry that follows it.
    let rejectEntry = null;
    let revertEntry = null;
    for (let i = history.length - 1; i >= 0; i--) {
        const entry = history[i];
        if (!revertEntry && entry.is_revert && entry.from_status === "rejected") {
            revertEntry = entry;
            continue;
        }
        if (revertEntry && entry.to_status === "rejected") {
            rejectEntry = entry;
            break;
        }
    }
    // If we found a revert without a paired reject in-history (candidate
    // was rejected before status_history existed), fall back to
    // rejection_meta so the CEO still sees something useful.
    if (revertEntry && !rejectEntry && candidate.rejection_meta) {
        rejectEntry = {
            to_status: "rejected",
            at: candidate.rejection_meta.at,
            by_name: candidate.rejection_meta.by_name,
            reason: candidate.rejection_meta.reason,
        };
    }
    if (!revertEntry || !rejectEntry) return null;

    const reasonLabel =
        (rejectEntry.reason && REASON_LABELS_MAP[rejectEntry.reason])
        || (rejectEntry.reason ? rejectEntry.reason.replace(/_/g, " ") : null);

    return (
        <div
            className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5 space-y-3"
            data-testid="rejection-audit-trail"
        >
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                Historique du refus
            </div>
            <ol className="space-y-2.5">
                <li className="flex items-start gap-3">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-red-100 text-red-600 shrink-0">
                        <ThumbsDown className="h-3.5 w-3.5" />
                    </span>
                    <div className="flex-1 min-w-0 leading-snug">
                        <div className="text-sm font-black text-shariti-black">
                            Refus initial
                            {rejectEntry.by_name && (
                                <span className="ml-1 font-medium text-slate-500">
                                    par {rejectEntry.by_name}
                                </span>
                            )}
                        </div>
                        <div className="text-[11px] font-semibold text-slate-500">
                            {rejectEntry.at && formatDateTime(rejectEntry.at)}
                            {reasonLabel && (
                                <span> · Motif&nbsp;: <strong className="text-red-700">{reasonLabel}</strong></span>
                            )}
                        </div>
                    </div>
                </li>
                <li className="flex items-start gap-3">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 shrink-0">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                    </span>
                    <div className="flex-1 min-w-0 leading-snug">
                        <div className="text-sm font-black text-emerald-800" data-testid="rejection-audit-undone">
                            Refus annulé
                            {revertEntry.by_name && (
                                <span className="ml-1 font-medium text-slate-500">
                                    par {revertEntry.by_name}
                                </span>
                            )}
                        </div>
                        <div className="text-[11px] font-semibold text-slate-500">
                            {revertEntry.at && formatDateTime(revertEntry.at)}
                            {revertEntry.to_status && (
                                <span> · Reprise à l&apos;étape <strong className="text-emerald-700">{revertEntry.to_status.replace(/_/g, " ")}</strong></span>
                            )}
                        </div>
                    </div>
                </li>
            </ol>
        </div>
    );
}
